/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import HEAttributeFooter from './components/HEAttributeFooter';
import RouteNames from '../../appAuthorized/RouteNames';
import AmountDescriptorControl from './components/AmountDescriptorControl';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../components/appNav/AppHeaderScrollable';
import { generalHealthEntryValue } from '../../../models/HealthEntryCompose';

import AmountInput from './components/AmountInput';
import ContentWrapper from '../../components/ContentWrapper';
import ContentLoader from '../../components/ContentLoader';
import { isValidHealthEntryValueAmount } from '../../../utilities/Validators';
import { useActiveUser } from '../../hooks/useUser';
import { useRelatedUnits } from '../../hooks/useMedli';
import { useHealthEntryAndValueForActiveProfile } from '../../hooks/useHealthEntry';
import { useNavigate } from '../../hooks/useNavigate';
import { useUIState } from '../../hooks/useUIState';
import HealthEntryModel from '../../../models/HealthEntry';

interface Props {
  match: any;
}

const HealthEntryValueAmount: React.FC<Props> = (props) => {
  const healthEntryId = Number(props.match.params.healthEntryId);
  const healthEntryValueId = Number(props.match.params.valueId);
  const [hevState, hevActions] = useHealthEntryAndValueForActiveProfile(healthEntryId, healthEntryValueId);

  const [{ preferredLanguageCode }] = useActiveUser();
  const [, uiStateActions] = useUIState();

  const cui = HealthEntryModel.getCUI(hevState.parentHealthEntry);
  const category = hevState.category;

  const [{ relatedUnitList }] = useRelatedUnits(category, cui);

  const pendingHeValue = hevActions.getEditing();

  const [, navActions] = useNavigate();
  const isDataLoaded = relatedUnitList.length > 0;
  const hasAmountValue = !!pendingHeValue.amount || !!pendingHeValue.description;

  const checkInvalid = (heData) => {
    const heValue = generalHealthEntryValue(category, heData, preferredLanguageCode);
    const v = (heValue.amount && heValue.amount.hasAmount()) ? (!isValidHealthEntryValueAmount(heValue.amount.getValue()) || !heValue.amount.hasUnit()) : false;
    return v;
  };

  const isInvalid = checkInvalid(pendingHeValue);

  const onChangeAmount = (amountData) => {
    const heValue = generalHealthEntryValue(category, pendingHeValue, preferredLanguageCode);
    heValue.amount.set(amountData);
    hevActions.updateEditing(heValue.data());

    uiStateActions.dismissNotification();
  };

  const submitForm = (d) => {
    hevActions.apiUpdate(d)
    .then(() => {
      navActions.goBackTo(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
      hevActions.resetEditing();
    }).catch(apiError => {
      uiStateActions.showErrorNotification(apiError.messageKey());
    });
  };

  const handleOnSelectDescriptor = (d) => {
    const heValue = generalHealthEntryValue(category, pendingHeValue, preferredLanguageCode);
    heValue.description.replaceWithSearchTerm(d);
    hevActions.updateEditing(heValue.data());
  };

  const handleOnRemove = () => {
    const heValue = generalHealthEntryValue(category, pendingHeValue, preferredLanguageCode);

    if (heValue.amount) {
      heValue.amount.clear();
    }
    if (heValue.description) {
      heValue.description.remove();
    }

    submitForm(heValue.data());
  };

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          submitDisabled={isInvalid}
          cancel={() => {
            hevActions.resetEditing();
            navActions.goBackTo(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
          }}
          submit={() => {
            submitForm(pendingHeValue);
          }}
          onDisabledClick={() => {
            const heValue = generalHealthEntryValue(category, pendingHeValue, preferredLanguageCode);
            if (heValue.amount && !heValue.amount.hasUnit()) {
              uiStateActions.showHintNotification('error.value-validator.no-selected-value-measure.message');
            }
          }}
          color="white"
        />

        <ContentFrame>
          <ScrollAwareScroller>
            <AppHeaderScrollableText
              id="heValue.title.text"
            />
            <ContentLoader isContentLoaded={isDataLoaded} isFixed={true} blockRenderUntilLoaded={true}>
            <ContentWrapper>
              <div>
                <AmountInput
                  cui={cui}
                  category={category}
                  healthEntryId={healthEntryId}
                  healthEntryValueId={healthEntryValueId}
                  amount={pendingHeValue.amount}
                  onChangeAmount={onChangeAmount}
                  allowCompositeUnit={true}
                  haveInitialFocus={false}
                  textPhrase={{
                    addUnit: {
                      id: 'heValue.unit.addUnit.label',
                    },
                    changeUnit: {
                      id: 'heValue.unit.changeUnit.label',
                    },
                    search: {
                      id: 'heValue.unit.search.label',
                    },
                    inputLabel: {
                      id: 'heValue.numericValue.field.label',
                    },
                  }}
                  valueValidation={(v) => {
                    return {
                      isValid: isValidHealthEntryValueAmount(v),
                      errorMessage: {
                        id: 'error.number-validator.invalid-value.message',
                      },
                    };
                  }}
                />

                <AmountDescriptorControl
                  description={pendingHeValue.description}
                  onSelectDescriptor={handleOnSelectDescriptor}
                  goDescriptorList={() => {
                    navActions.goTo(RouteNames.healthEntryValueDescriptor(healthEntryId, healthEntryValueId));
                  }}
                />
              </div>

              {
                hasAmountValue && (
                  <HEAttributeFooter
                    onDelete={handleOnRemove}
                    onToggleFlag={null}
                  />
                )
              }

            </ContentWrapper>
            </ContentLoader>
          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

export default HealthEntryValueAmount;
